import { Moment } from 'moment';
import Customer from '@/customer/models/Customer';
import CleaningStatus from '@/dossier/constants/CleaningStatus';
import VehicleType from '@/shared/constants/VehicleType';
import Brand from '@/shared/models/Brand';
import { getDefaultCountryId } from '@/shared/models/Country';
import { Url } from '@/shared/models/common';
import Carline from './Carline';
import Location from './Location';
import ServiceHistory from './ServiceHistory';
import VehicleData from './VehicleData';
import { isPresent } from '@/shared/utils/typescript';

/**
 * Vehicles are stored in the backend database in two different tables, which have separate IDs
 */
export interface VehicleId {__vehicleId: number}
export interface VehicleMasterId {__vehicleMasterId: number}
export type RegistrationNumber = string;
export type ObjectIdentifier = string;

interface PlannerVehicleData {
  brand: Brand | null;
  modelDescription: string;
  registrationNumber: RegistrationNumber;
  contact: string;
}

export class KeyLabel {
  content: string | null = '';
  qr: string | null = '';
}

export default class Vehicle {
  id: VehicleId | null = null;
  masterId: VehicleMasterId | null = null;
  type: VehicleType | null = null;
  mileage: number | null = null;
  isVerifiedMileage = false;
  mileageDate: Moment | null = null;
  odometerImage: Url | null = null;
  registrationNumber: RegistrationNumber = '';
  identifier: string | null = null;
  externalReference: string | null = null;
  externalReferenceId: number | null = null;
  image: string | null = null;
  estimatedYearlyMileage: number | null = null;
  objectIdentifier: ObjectIdentifier | null = null;
  modelDescription = '';
  outletId: number | null = null;
  intervalTime: number | null = null;
  intervalMileageKm: number | null = null;
  modelYear: string | null = null;
  modelCode: string | null = null;
  engineCode: string | null = null;
  avatar: Url | null = null;
  isCustomer = false;
  isAllowedBrand = false;
  cleaningStatus: CleaningStatus | null = null;
  keyLabel: KeyLabel = {
    content: '',
    qr: '',
  };

  location: Location | null = null;
  brand: Brand | null = null;
  carline: Carline | null = null;
  customers: Array<Customer> = [];
  serviceHistory: ServiceHistory | null = null;
  registrationDate: Moment | null = null;
  exteriorColor: string | null = null;
  endOfWarranty: Moment | null = null;
  endOfExtendedWarranty: Moment | null = null;
  countryId = getDefaultCountryId();
  connection: string | null = null;

  get hasId(): boolean {
    return this.id !== null;
  }

  static forPlannerEvent(data: PlannerVehicleData): Vehicle {
    const vehicle = new Vehicle();

    vehicle.brand = data.brand;
    vehicle.registrationNumber = data.registrationNumber;
    vehicle.modelDescription = data.modelDescription;

    if (data.contact) {
      const customer = Customer.withFullName(data.contact);
      vehicle.customers = [customer];
    }

    return vehicle;
  }

  static withData(data: Partial<Vehicle>): Vehicle {
    return Object.assign(new Vehicle(), data);
  }

  update(data: VehicleData, location: Location): void {
    this.mileage = data.mileage;
    this.isVerifiedMileage = data.isVerifiedMileage;
    this.location = location;
    if (data.keyLabel !== undefined) this.keyLabel.content = data.keyLabel;
    if (isPresent(data.countryId)) this.countryId = data.countryId;
  }

  updateWith(that: Vehicle): void {
    this.mileage = that.mileage;
    this.isVerifiedMileage = that.isVerifiedMileage;
    this.location = that.location;
    this.keyLabel = that.keyLabel;
  }
}
